import Vue from 'vue';
import { getActiveUser } from '@/utils';
import { DEFAULT_THEME, THEMES, THEME_NAMES, ThemeName } from './themes';

const STORAGE_PREFIX = 'sophia.theme.';

const state = Vue.observable<{ name: ThemeName }>({ name: DEFAULT_THEME });

/** Read-only view of the active theme for templates (`$appTheme`). Reactive. */
export const appTheme = {
  get name() { return state.name; },
  get isSophia() { return state.name === 'sophia'; },
  get tabsColor() { return THEMES[state.name].tabsColor; },
  get tabsSlider() { return THEMES[state.name].tabsSlider; },
  get chartBar() { return THEMES[state.name].chartBar; },
  get chartHighlight() { return THEMES[state.name].chartHighlight; },
  get fontFamily() { return THEMES[state.name].fontFamily; },
};

export type AppTheme = typeof appTheme;

export const isThemeName = (value: unknown): value is ThemeName =>
  typeof value === 'string' && (THEME_NAMES as string[]).includes(value);

const storageKey = (email?: string) => `${STORAGE_PREFIX}${email ?? getActiveUser()}`;

/** Theme chosen by this account (defaults to the active account of the tab); Classic when unset or invalid. */
export const getStoredTheme = (email?: string): ThemeName => {
  try {
    const stored = localStorage.getItem(storageKey(email));
    return isThemeName(stored) ? stored : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
};

export const setStoredTheme = (email: string, name: ThemeName): void => {
  try {
    localStorage.setItem(storageKey(email), name);
  } catch {
    // storage unavailable (private mode): the choice only lasts for this page load
  }
};

/** Switch the live theme: CSS scope, Vuetify palette and the reactive state used by templates. */
export const applyTheme = (name: ThemeName): void => {
  state.name = name;
  document.documentElement.setAttribute('data-theme', name);
  const vuetifyTheme = (Vue.prototype.$vuetify as { theme?: Record<string, string> } | undefined)?.theme;
  if (vuetifyTheme) {
    Object.assign(vuetifyTheme, THEMES[name].palette);
  }
};
